/* eslint-disable no-throw-literal */
/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { useImmer } from "use-immer";
import { AppContext, AuthContext } from "../../../store";
import { useDropdownApis } from "../../../shared";
import { useLocation } from "react-router-dom";
import { getPlAndBsDashboardDetailsApi } from "../apis";
import { arraySort, errorMessage } from "../../../utils";
import moment from "moment";

export const usePLDashboardList = () => {
  const {
    appState: { globalData },
  } = React.useContext(AppContext);
  const { auth } = React.useContext(AuthContext);

  const [state, setState] = useImmer({
    initialLoad: false,
    charts: {},
    filter: {
      options: {
        stores: [],
        financialYears: [],
        accountMonths: [],
        ratios: [],
      },
      values: { store: [], financialYear: null, month: [], ratio: [] },
      isFilterApplied: true,
    },
    chartLoading: true,
  });

  const location = useLocation();
  const isStoreAdmin = location.pathname.includes("store-admin");

  const { franchiseeWiseStoreList } = useDropdownApis({
    isFranchiseeWiseStoreList: !isStoreAdmin,
  });

  const getPlAndBsDashboardDetails = async (formData) => {
    triggerChartLoading(true);
    try {
      const response = await getPlAndBsDashboardDetailsApi(formData);

      const { success, data, message } = response;

      if (success) {
        setState((draft) => {
          if (Object.entries(data).length > 0) {
            draft.charts = data;
          }
          if (state.initialLoad) {
            draft.filter.isFilterApplied = true;
          }
        });
      } else {
        throw { response: { data: { message } } };
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ?? "Failed to fetch dashboard details";

      errorMessage(message);
    } finally {
      triggerChartLoading(false);
    }
  };

  const triggerChartLoading = (status) => {
    setState((draft) => {
      draft.chartLoading = status;
    });
  };

  const handleChangeFilter = ({ name, value }) => {
    setState((draft) => {
      draft.filter.values[name] = value;
      draft.filter.isFilterApplied = false;
    });
  };

  const getFilterParams = async (filterValues) => {
    const formData = {};

    Object.entries(filterValues).forEach(([key, values]) => {
      const actualKey =
        key === "financialYear"
          ? "year"
          : key === "store"
          ? "store_id"
          : key === "ratio"
          ? "ratios"
          : key;

      formData[actualKey] = Array.isArray(values)
        ? values.map((value) => value.value)
        : [values.value];
    });

    if (isStoreAdmin && auth?.data?.selectedStoreId) {
      formData.store_id = [auth.data.selectedStoreId];
    }

    return formData;
  };

  const handleApplyFilter = async () => {
    resetChartData();
    const formData = await getFilterParams(state.filter.values);
    await getPlAndBsDashboardDetails(formData);
  };

  function getChartTitle(key) {
    const ratio = state.filter.options.ratios.find(
      (ratio) => ratio.value?.toLowerCase() === key?.toLowerCase()
    );
    return ratio ? ratio.label : "";
  }

  const resetChartData = () => {
    setState((draft) => {
      draft.charts = {};
    });
  };

  React.useEffect(() => {
    if (!isStoreAdmin) {
      const stores = [
        ...franchiseeWiseStoreList.map((store, index) => ({
          label: store.name,
          value: store.id,
          franchisee_id: store.franchisee_id,
          franchisee_name: store.franchisee_name,
        })),
      ];
      setState((draft) => {
        if (franchiseeWiseStoreList.length > 0) {
          const sortedArray = arraySort(stores, "franchisee_name");
          draft.filter.options.stores = sortedArray;
          draft.filter.values.store = sortedArray;
        }
      });
    }
  }, [franchiseeWiseStoreList]);

  React.useEffect(() => {
    setState((draft) => {
      if (globalData?.pl_bs_reports?.pl_account_ratios) {
        draft.filter.options.ratios =
          globalData.pl_bs_reports.pl_account_ratios;
        draft.filter.values.ratio = globalData.pl_bs_reports.pl_account_ratios;
      }
      if (globalData?.pl_bs_reports?.pl_bs_accounts_months) {
        const months = globalData.pl_bs_reports.pl_bs_accounts_months.map(
          (month) => {
            const updatedMonth = {
              ...month,

              quarter:
                month.value <= 3
                  ? "Q1"
                  : month.value <= 6
                  ? "Q2"
                  : month.value <= 9
                  ? "Q3"
                  : "Q4",
            };
            if (month.label.includes(moment().format("MMMM"))) {
              draft.filter.values.month = [updatedMonth];
            }
            return updatedMonth;
          }
        );

        draft.filter.options.accountMonths = months;
      }

      if (globalData?.pl_bs_reports?.pl_bs_accounts_years) {
        const years = globalData.pl_bs_reports.pl_bs_accounts_years;
        const currentFinancialYear = years.find(
          (year) => year.value.split("-")[1] === moment().format("YYYY")
        );
        if (currentFinancialYear) {
          draft.filter.values.financialYear = currentFinancialYear;
        }
        draft.filter.options.financialYears = years;
      }
    });
  }, [globalData]);

  React.useEffect(() => {
    if (
      state.filter.values.ratio.length > 0 &&
      state.filter.values.month.length > 0 &&
      (state.filter.values.store.length > 0 || isStoreAdmin) &&
      state.filter.values.financialYear !== null &&
      state.initialLoad === false
    )
      getFilterParams(state.filter.values).then((formData) => {
        getPlAndBsDashboardDetails(formData);
        setState((draft) => {
          draft.initialLoad = true;
        });
      });
  }, [state.filter.values]);

  return {
    state,
    isStoreAdmin,
    getChartTitle,
    handleChangeFilter,
    handleApplyFilter,
  };
};
